// ==========================================================================
// lib/rental.ts — agent statements + the rental ledger (FF-036 part 2, 9 Oct 2026)
//   James: "we should have another arm of inputing rental info than a bank CSV
//   file, but we dont want double up if the CSV is then loaded but a rental
//   ledger being read by a worker is also fine and a good way to manualy input
//   and agian no double ups."
//
//   Rent, fees and repairs happen in the agent's trust account; only the net
//   "Payment to Owner" reaches the bank. So the property's own lines live in
//   property_ledger (from a statement the reader read, or typed in), and the
//   bank deposit is coded 945 "Rent paid out by agent" (a money movement) and
//   linked to the payout line. No double ups, three ways:
//     1. every ledger line has a fingerprint (date + signed amount + which one
//        of that pair on the day); the database refuses a second copy, so an
//        overlapping statement or a line typed twice is skipped;
//     2. payouts are matched to bank deposits (same amount, within 7 days) and
//        the deposit is coded 945, so rent is counted once, from the ledger;
//     3. the totals in the database add ledger lines except payouts.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";
import type { Category } from "./money";
import { shrink, worker } from "./receipts";

export type LedgerKind = "rent" | "fee" | "expense" | "recovery" | "payout" | "other";
export const LEDGER_KINDS: { value: LedgerKind; label: string; sign: 1 | -1 | 0; hint: string }[] = [
  { value: "rent", label: "Rent", sign: 1, hint: "Rent the tenant paid" },
  { value: "fee", label: "Agent fee", sign: -1, hint: "Management, letting or admin fees" },
  { value: "expense", label: "Expense", sign: -1, hint: "Repairs, rates, water, insurance the agent paid" },
  { value: "recovery", label: "Paid back", sign: 1, hint: "The tenant paid a cost back, e.g. water usage" },
  { value: "payout", label: "Paid to us", sign: -1, hint: "The agent's payment to our bank account" },
  { value: "other", label: "Other", sign: 0, hint: "Anything else" },
];
export const kindLabel = (k: LedgerKind) => LEDGER_KINDS.find((x) => x.value === k)?.label ?? k;

export type StatementRead = {
  period_from: string | null; period_to: string | null; statement_no: string | null; rent_per_week: number | null;
  totals?: { incoming?: number | null; expenses?: number | null; fees?: number | null; paid_to_owner?: number | null };
  lines: { date: string; kind: LedgerKind; description: string; amount: number; gst: number | null; code: string | null }[];
};
export type RentalStatement = {
  id: string; asset_id: string; file_name: string | null; mime: string | null; uploaded: boolean;
  period_from: string | null; period_to: string | null; statement_no: string | null;
  ai_read_at: string | null; ai_suggested: StatementRead | null; approved_at: string | null;
  lines_added: number | null; lines_skipped: number | null; created_at: string;
};
export type LedgerLine = {
  id: string; asset_id: string; statement_id: string | null; entry_date: string; kind: LedgerKind; category_id: string | null;
  amount_cents: number; gst_cents: number | null; description: string; fingerprint: string; transaction_id: string | null; created_at: string;
};
/** One line on the review sheet or the add-a-line form, before it's saved. */
export type DraftLine = {
  key: string; include: boolean; entry_date: string; kind: LedgerKind; category_id: string | null;
  amount_cents: number; gst_cents: number | null; description: string;
};

const fpBase = (date: string, cents: number) => `${date}|${cents}`;

/** Fingerprints for a batch: the second identical line on the same day gets #2, and so on. */
export function fingerprints(lines: { entry_date: string; amount_cents: number }[]): string[] {
  const seen = new Map<string, number>();
  return lines.map((l) => { const b = fpBase(l.entry_date, l.amount_cents); const n = (seen.get(b) ?? 0) + 1; seen.set(b, n); return `${b}#${n}`; });
}

/** Sign a positive amount by kind ("other" keeps the sign it has). */
export function signFor(kind: LedgerKind, cents: number) {
  const s = LEDGER_KINDS.find((k) => k.value === kind)?.sign ?? 0;
  return s === 0 ? cents : s * Math.abs(cents);
}

/** Reader output → draft lines, with codes turned into budget code ids. */
export function draftsFrom(read: StatementRead, cats: Category[]): DraftLine[] {
  const byCode = (code: string | null) => (code ? cats.find((c) => !c.is_group && c.code === code && !c.archived_at)?.id ?? null : null);
  return (read.lines ?? []).map((l, i) => {
    const kind = (LEDGER_KINDS.some((k) => k.value === l.kind) ? l.kind : "other") as LedgerKind;
    const cents = Math.round(Math.abs(Number(l.amount) || 0) * 100);
    return {
      key: `r${i}`, include: cents > 0, entry_date: l.date, kind,
      category_id: kind === "payout" ? null : byCode(l.code) ?? byCode(kind === "rent" ? "140" : kind === "fee" ? "810" : kind === "expense" ? "845" : null),
      amount_cents: signFor(kind, kind === "other" ? -cents : cents),
      gst_cents: l.gst ? Math.round(Math.abs(l.gst) * 100) : null, description: (l.description ?? "").slice(0, 120),
    };
  });
}

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { for (const k of ["rental", "split_totals", "txns", "unreconciled", "rec_summary", "audit"]) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

export function useRentalStatements(assetId: string | undefined) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rental", hid, "statements", assetId], enabled: !!hid && !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase.from("property_statements")
        .select("id,asset_id,file_name,mime,uploaded,period_from,period_to,statement_no,ai_read_at,ai_suggested,approved_at,lines_added,lines_skipped,created_at")
        .eq("asset_id", assetId!).is("archived_at", null).order("created_at", { ascending: false });
      if (error) throw error;
      return data as RentalStatement[];
    },
  });
}

/** Ledger lines for one property in a period (or all, for the household, when assetId is omitted). */
export function useLedger(assetId: string | undefined, from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rental", hid, "ledger", assetId ?? "all", from, to], enabled: !!hid,
    queryFn: async () => {
      const out: LedgerLine[] = [];
      for (let at = 0; ; at += 1000) {
        let q = supabase.from("property_ledger").select("id,asset_id,statement_id,entry_date,kind,category_id,amount_cents,gst_cents,description,fingerprint,transaction_id,created_at")
          .eq("household_id", hid).is("deleted_at", null).gte("entry_date", from).lte("entry_date", to);
        if (assetId) q = q.eq("asset_id", assetId);
        const { data, error } = await q.order("entry_date", { ascending: false }).range(at, at + 999);
        if (error) throw error;
        out.push(...(data as LedgerLine[]).map((l) => ({ ...l, amount_cents: Number(l.amount_cents), gst_cents: l.gst_cents === null ? null : Number(l.gst_cents) })));
        if ((data ?? []).length < 1000) break;
      }
      return out;
    },
  });
}

/** Upload agent statements for a property: a row first, then the file to the Worker. */
export function useAddStatements() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async (a: { assetId: string; files: File[] }) => {
      const failed: string[] = []; const ids: string[] = [];
      for (const f of a.files) {
        const id = crypto.randomUUID();
        const body = await shrink(f);
        const type = body.type || f.type || (f.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "");
        const { error } = await supabase.from("property_statements").insert({ id, household_id: hid, asset_id: a.assetId, file_key: `${hid}/statements/${id}`, file_name: f.name, uploaded_by: me?.id ?? null });
        if (error) { failed.push(f.name); continue; }
        const r = await worker(`/statements/${id}/file`, { method: "PUT", headers: { "Content-Type": type }, body });
        if (!r.ok) {
          const j = await r.json().catch(() => ({})) as { message?: string };
          failed.push(`${f.name}${j.message ? ` (${j.message})` : ""}`);
          await supabase.from("property_statements").update({ archived_at: new Date().toISOString() }).eq("id", id);
          continue;
        }
        ids.push(id);
      }
      if (failed.length) throw new Error(`Couldn't add: ${failed.join(", ")}`);
      return ids;
    },
    onSettled: inv,
  });
}

/** Ask the reader to read a statement. Saves only its suggestion; nothing reaches the ledger until Approve. */
export async function readStatement(id: string): Promise<StatementRead> {
  const r = await worker(`/statements/${id}/read`, { method: "POST" });
  const j = (await r.json().catch(() => ({}))) as { suggested?: StatementRead; message?: string };
  if (!r.ok || !j.suggested) throw new Error(j.message ?? "The reader couldn't read it.");
  return j.suggested;
}

export function useArchiveStatement() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("property_statements").update({ archived_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** Lines already in the ledger for these dates, by fingerprint (to flag doubles before saving). */
export async function existingFingerprints(assetId: string, dates: string[]): Promise<Map<string, LedgerLine>> {
  if (!dates.length) return new Map();
  const sorted = [...dates].sort();
  const { data, error } = await supabase.from("property_ledger").select("id,asset_id,statement_id,entry_date,kind,category_id,amount_cents,gst_cents,description,fingerprint,transaction_id,created_at")
    .eq("asset_id", assetId).is("deleted_at", null).gte("entry_date", sorted[0]).lte("entry_date", sorted.at(-1)!);
  if (error) throw error;
  return new Map((data as LedgerLine[]).map((l) => [l.fingerprint, { ...l, amount_cents: Number(l.amount_cents) }]));
}

function checkLine(l: DraftLine) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(l.entry_date)) return "a date";
  if (!l.amount_cents) return "an amount";
  if (l.kind !== "payout" && !l.category_id) return "a budget code";
  if (l.kind === "payout" && l.amount_cents > 0) return "a payout as money out of the agent's account";
  if (!l.description.trim()) return "a description";
  return null;
}

/** Save the ticked lines of a statement to the ledger, skipping any already there. */
export function useApproveStatement() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async (a: { statement: RentalStatement; lines: DraftLine[]; period_from: string | null; period_to: string | null; statement_no: string | null }) => {
      const keep = a.lines.filter((l) => l.include);
      for (const l of keep) { const miss = checkLine(l); if (miss) throw new Error(`"${l.description || l.entry_date}" needs ${miss}.`); }
      const fps = fingerprints(keep);
      const have = await existingFingerprints(a.statement.asset_id, keep.map((l) => l.entry_date));
      let added = 0; let skipped = 0;
      for (let i = 0; i < keep.length; i++) {
        const l = keep[i];
        if (have.has(fps[i])) { skipped++; continue; }
        const { error } = await supabase.from("property_ledger").insert({
          household_id: hid, asset_id: a.statement.asset_id, statement_id: a.statement.id, entry_date: l.entry_date, kind: l.kind,
          category_id: l.kind === "payout" ? null : l.category_id, amount_cents: l.amount_cents, gst_cents: l.gst_cents, description: l.description.trim(), fingerprint: fps[i],
        });
        if (error) { if (error.code === "23505") { skipped++; continue; } throw error; }
        added++;
      }
      const { error } = await supabase.from("property_statements").update({
        approved_at: new Date().toISOString(), lines_added: added + (a.statement.lines_added ?? 0), lines_skipped: skipped,
        period_from: a.period_from, period_to: a.period_to, statement_no: a.statement_no,
      }).eq("id", a.statement.id);
      if (error) throw error;
      return { added, skipped };
    },
    onSettled: inv,
  });
}

/** Type a line in by hand. Refused if the same line is already there. */
export function useAddLedgerLine() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async (a: { assetId: string; line: DraftLine }) => {
      const miss = checkLine(a.line); if (miss) throw new Error(`Needs ${miss}.`);
      const fp = `${fpBase(a.line.entry_date, a.line.amount_cents)}#1`;
      const have = await existingFingerprints(a.assetId, [a.line.entry_date]);
      const dup = have.get(fp);
      if (dup) throw new Error(`Already in the ledger: "${dup.description}" on that date for that amount.`);
      const { error } = await supabase.from("property_ledger").insert({
        household_id: hid, asset_id: a.assetId, statement_id: null, entry_date: a.line.entry_date, kind: a.line.kind,
        category_id: a.line.kind === "payout" ? null : a.line.category_id, amount_cents: a.line.amount_cents, gst_cents: a.line.gst_cents,
        description: a.line.description.trim(), fingerprint: fp,
      });
      if (error) throw error.code === "23505" ? new Error("Already in the ledger: a line on that date for that amount.") : error;
    },
    onSuccess: inv,
  });
}

/** Take a line out of the ledger (kept for the audit trail, just not counted). */
export function useRemoveLedgerLine() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("property_ledger").update({ deleted_at: new Date().toISOString(), transaction_id: null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

// ---------------------------------------------------------------------------
// Payouts ↔ bank deposits
// ---------------------------------------------------------------------------
export type BankDeposit = {
  id: string; account_id: string; posted_on: string; amount_cents: number; description: string; status: string;
  transfer_pair_id: string | null; contact_id: string | null; transaction_splits: { category_id: string; asset_id: string | null }[];
};
export type PayoutMatch = { payout: LedgerLine; deposit: BankDeposit; state: "unreconciled" | "is945" | "coded_other" };

/** Payouts in the ledger with no bank deposit linked yet, each with its likely deposit (same amount, within 7 days). */
export function usePayoutMatches(assetId: string | undefined, code945: string | undefined) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rental", hid, "payout_matches", assetId, code945], enabled: !!hid && !!assetId,
    queryFn: async () => {
      const { data: p, error } = await supabase.from("property_ledger").select("id,asset_id,statement_id,entry_date,kind,category_id,amount_cents,gst_cents,description,fingerprint,transaction_id,created_at")
        .eq("asset_id", assetId!).eq("kind", "payout").is("transaction_id", null).is("deleted_at", null).order("entry_date").limit(500);
      if (error) throw error;
      const payouts = (p as LedgerLine[]).map((l) => ({ ...l, amount_cents: Number(l.amount_cents) }));
      if (!payouts.length) return [] as PayoutMatch[];
      return matchDeposits(hid!, payouts, code945);
    },
  });
}

async function matchDeposits(hid: string, payouts: LedgerLine[], code945: string | undefined): Promise<PayoutMatch[]> {
  const day = 86400000;
  const dates = payouts.map((x) => new Date(x.entry_date).getTime());
  const from = new Date(Math.min(...dates) - 7 * day).toISOString().slice(0, 10); const to = new Date(Math.max(...dates) + 7 * day).toISOString().slice(0, 10);
  const amounts = [...new Set(payouts.map((x) => -x.amount_cents))];
  const [t, linked] = await Promise.all([
    supabase.from("transactions").select("id,account_id,posted_on,amount_cents,description,status,transfer_pair_id,contact_id,transaction_splits(category_id,asset_id)")
      .eq("household_id", hid).is("deleted_at", null).in("amount_cents", amounts).gte("posted_on", from).lte("posted_on", to),
    supabase.from("property_ledger").select("transaction_id").eq("household_id", hid).not("transaction_id", "is", null),
  ]);
  if (t.error) throw t.error; if (linked.error) throw linked.error;
  const taken = new Set((linked.data as { transaction_id: string }[]).map((x) => x.transaction_id));
  const deps = (t.data as unknown as BankDeposit[]).map((d) => ({ ...d, amount_cents: Number(d.amount_cents) })).filter((d) => !taken.has(d.id) && !d.transfer_pair_id);
  const out: PayoutMatch[] = [];
  for (const p of payouts) {
    const pd = new Date(p.entry_date).getTime();
    const d = deps.filter((x) => x.amount_cents === -p.amount_cents && Math.abs(new Date(x.posted_on).getTime() - pd) <= 7 * day && !out.some((o) => o.deposit.id === x.id))
      .sort((a, b) => Math.abs(new Date(a.posted_on).getTime() - pd) - Math.abs(new Date(b.posted_on).getTime() - pd))[0];
    if (!d) continue;
    const s = d.transaction_splits;
    const state = s.length === 0 ? "unreconciled" : s.length === 1 && s[0].category_id === code945 ? "is945" : "coded_other";
    out.push({ payout: p, deposit: d, state });
  }
  return out;
}

/** Code each deposit as 945 for the property (keeping its contact) and link it to its payout. */
export function useLinkPayouts() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (a: { matches: PayoutMatch[]; code945: string }) => {
      for (const m of a.matches) {
        if (m.state !== "is945" || m.deposit.transaction_splits[0]?.asset_id !== m.payout.asset_id) {
          const { error } = await supabase.rpc("code_transaction", {
            p_transaction_id: m.deposit.id, p_note: null, p_contact_id: m.deposit.contact_id,
            p_splits: [{ amount_cents: m.deposit.amount_cents, category_id: a.code945, tracker_id: "", asset_id: m.payout.asset_id, note: "" }],
          });
          if (error) throw error;
        }
        const { error } = await supabase.from("property_ledger").update({ transaction_id: m.deposit.id }).eq("id", m.payout.id);
        if (error) throw error;
      }
    },
    onSuccess: inv,
  });
}

/** For Reconcile: payouts not yet linked, across every property. */
export function useOpenPayouts() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rental", hid, "open_payouts"], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("property_ledger").select("id,asset_id,statement_id,entry_date,kind,category_id,amount_cents,gst_cents,description,fingerprint,transaction_id,created_at")
        .eq("household_id", hid).eq("kind", "payout").is("transaction_id", null).is("deleted_at", null).limit(1000);
      if (error) throw error;
      return (data as LedgerLine[]).map((l) => ({ ...l, amount_cents: Number(l.amount_cents) }));
    },
  });
}

/** Bank lines coded 140 Rent for a property the agent manages are the payout
 *  arriving (from the agent, or forwarded on, e.g. by Caitlin): recode them to
 *  945 so the rent is only counted from the agent's statements. Keeps the
 *  property and the contact. Lines split across several codes are left alone. */
export function useRecodeRentToPayout() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (a: { transactionIds: string[]; assetId: string; code945: string }) => {
      const { data, error } = await supabase.from("transactions").select("id,amount_cents,contact_id,transaction_splits(id)").in("id", a.transactionIds);
      if (error) throw error;
      let done = 0; let skipped = 0;
      for (const t of data as { id: string; amount_cents: number; contact_id: string | null; transaction_splits: { id: string }[] }[]) {
        if (t.transaction_splits.length !== 1 || Number(t.amount_cents) <= 0) { skipped++; continue; }
        const { error: e } = await supabase.rpc("code_transaction", {
          p_transaction_id: t.id, p_note: null, p_contact_id: t.contact_id,
          p_splits: [{ amount_cents: Number(t.amount_cents), category_id: a.code945, tracker_id: "", asset_id: a.assetId, note: "" }],
        });
        if (e) throw e;
        done++;
      }
      return { done, skipped };
    },
    onSuccess: inv,
  });
}
